import { cardDraftHasChanges } from '../lib/racquetCardDraft'
import { acquireModalScrollLock } from '../lib/modalScrollLock'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ChevronRight, Trash2 } from 'lucide-react'
import { golfDeleteRevealStyle } from '../lib/letsPlayGolfSwipe'
import { racquetPath, racquetResponse } from '../lib/racquetApi'
import { formatDiningSlotTime } from '../utils/diningPresentation'

// Never discard another tab's unsynchronised work as a side effect of removal.
export function hasRacquetLocalWork(storage, clubCode, matchId) {
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i)
    if (!(key?.startsWith('privana:racquet-card:') || key?.startsWith('privana:racquet-events:')) || !key.endsWith(`:${encodeURIComponent(matchId)}`)) continue
    if (!key.includes(`:${encodeURIComponent(clubCode)}:`)) continue
    const value = JSON.parse(storage.getItem(key))
    if (key.startsWith('privana:racquet-card:')) {
      // Consent/focus metadata alone is not unsynchronised scoring work.
      // Preserve conservative handling of legacy or unreadable drafts.
      if (value?.version !== 1 || !value.baseValues || cardDraftHasChanges(value, {})) return true
    } else if (!Array.isArray(value?.events) || value.events.length || value?.version !== 1) return true
  }
  return false
}

export default function RacquetContinueSessions({ clubCode, sport, matches, t, locale, post, navigate, onRemoved }) {
  const [open, setOpen] = useState(null), [offset, setOffset] = useState(0)
  const [confirm, setConfirm] = useState(null), [pending, setPending] = useState(false), [error, setError] = useState('')
  const gesture = useRef(null), suppress = useRef(false), attempt = useRef(null), lock = useRef(false)
  const ask = (match) => { setError(''); setConfirm(match); setOpen(null); setOffset(0); attempt.current = null }
  const dialog = useRef(null)
  useEffect(() => {
    if (!confirm) return
    const release = acquireModalScrollLock('racquet-remove-session')
    const previous = document.activeElement
    const key = e => {
      if (e.key === 'Escape' && !lock.current) { setConfirm(null); setError('') }
      if (e.key !== 'Tab') return
      const controls = [...dialog.current.querySelectorAll('button:not(:disabled)')]
      const first = controls[0], last = controls.at(-1)
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last?.focus() }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first?.focus() }
    }
    document.addEventListener('keydown', key)
    return () => { document.removeEventListener('keydown', key); release(); previous?.focus?.() }
  }, [confirm])
  const close = () => { if (!lock.current) { setConfirm(null); setError('') } }
  const remove = async () => {
    if (lock.current) return
    try {
      if (hasRacquetLocalWork(localStorage, clubCode, confirm.id)) { setError('deletePending'); return }
    } catch { setError('deletePending'); return }
    lock.current = true; setPending(true); setError('')
    try {
      attempt.current ||= { id: crypto.randomUUID(), expectedRevision: confirm.revision }
      await post(racquetPath(clubCode, `/matches/${confirm.id}/remove`), attempt.current).then(racquetResponse)
      // The server accepted removal; discard stale read caches, never pending work.
      try { for (const key of Object.keys(localStorage)) if (key.startsWith(`privana:racquet-match:v1:${encodeURIComponent(clubCode)}:`) && key.endsWith(`:${confirm.id}`)) localStorage.removeItem(key) } catch {}
      onRemoved(confirm.id); setConfirm(null)
    } catch (e) { setError(e.status === 409 ? 'deleteChanged' : 'error') }
    finally { lock.current = false; setPending(false) }
  }
  return <section className="rp-continue">
    <h2>{t('resumeMatches')}</h2>
    {matches.map(m => {
      const x = open === m.id ? offset : 0
      const racket = ['tennis', 'padel'].includes(sport)
      const score = racket ? `${t('sets')} ${m.state.sets.join('–')} · ${t('games')} ${m.state.games.join('–')}` : `${t('games')} ${m.state.segments.length} · ${t('points')} ${m.state.points.join('–')}`
      const zone = m.timeZone || 'UTC', date = new Date(m.started_at)
      return <div key={m.id}>
        <div className="mp-golf-resume">
          {m.can_delete && <div className="mp-golf-resume__reveal" style={golfDeleteRevealStyle(x)} aria-hidden={x !== -80}>
            <button className="mp-golf-resume__delete" tabIndex={x === -80 ? 0 : -1} aria-label={`${t('deleteSession')} · ${m.court_name}`} onClick={() => ask(m)}><Trash2 size={20} /><span>{t('remove')}</span></button>
          </div>}
          <button className="mp-golf-resume__row" style={{ transform: `translateX(${x}px)` }}
            onKeyDown={e => { if (m.can_delete && ['ArrowLeft', 'Delete'].includes(e.key)) { e.preventDefault(); setOpen(m.id); setOffset(-80) } if (e.key === 'Escape') { setOpen(null); setOffset(0) } }}
            onPointerDown={e => { suppress.current = false; if (m.can_delete) gesture.current = { x: e.clientX, y: e.clientY, start: x, offset: x, axis: null } }}
            onPointerMove={e => {
              const g = gesture.current; if (!g) return
              const dx = e.clientX - g.x, dy = e.clientY - g.y
              if (!g.axis && Math.max(Math.abs(dx), Math.abs(dy)) > 10) { g.axis = Math.abs(dx) > Math.abs(dy) * 1.2 ? 'x' : 'y'; if (g.axis === 'x') e.currentTarget.setPointerCapture?.(e.pointerId) }
              if (g.axis !== 'x') return
              e.preventDefault(); g.offset = Math.max(-80, Math.min(0, g.start + dx)); setOpen(m.id); setOffset(g.offset)
            }}
            onPointerUp={() => { const g = gesture.current; if (!g) return; suppress.current = Boolean(g.axis); if (g.axis === 'x') { setOpen(g.offset < -38 ? m.id : null); setOffset(g.offset < -38 ? -80 : 0) } gesture.current = null }}
            onPointerCancel={() => { suppress.current = true; gesture.current = null; setOpen(null); setOffset(0) }}
            onClick={() => { if (suppress.current) { suppress.current = false; return } if (open) { setOpen(null); setOffset(0); return } navigate(`/${clubCode}/lets-play/${sport}/match/${m.id}`) }}>
            <span><strong title={m.court_name}>{m.court_name}</strong><small>{new Intl.DateTimeFormat(locale, { timeZone: zone, dateStyle: 'medium' }).format(date)} · {formatDiningSlotTime(m.started_at, zone, locale)}</small><b>{score}</b><small>{t(m.state.teams[0].players.length === 2 ? 'doubles' : 'singles')} · {t(m.status)}</small></span><ChevronRight aria-hidden="true" />
          </button>
        </div>
      </div>
    })}
    {confirm && createPortal(<div className="rp rp-overlay"><section ref={dialog} className="rp-dialog" role="dialog" aria-modal="true" aria-label={t('deleteSession')} onKeyDown={e => { if (e.key === 'Escape') close() }}>
      <h2>{t('deleteSession')}</h2><p>{confirm.court_name}</p><p>{t('deleteNote')}</p>
      {error && <p role="alert">{t(error)}</p>}
      {['deletePending', 'deleteChanged'].includes(error) && <button onClick={() => navigate(`/${clubCode}/lets-play/${sport}/match/${confirm.id}`)}>{t('resume')}</button>}
      <button autoFocus disabled={pending} onClick={close}>{t('cancel')}</button><button disabled={pending || ['deletePending', 'deleteChanged'].includes(error)} onClick={remove}>{t(pending ? 'deleting' : 'deleteSession')}</button>
    </section></div>, document.body)}
  </section>
}
